import { trpc } from "../../main/components/ProviderContainer";
import FactureComp from "../components/FactureComp";
import DepenseLine from "../components/DepenseLine";
import _ from "underts";
import { useLocation, useSearchParams } from "wouter";
import { useEffect, useRef } from "react";
import { floatStringToIntegerCent } from "vincent-utils";

export function Examiner(props: {
  year: number;
  onSetYear: (year: number) => void;
}) {
  const [location, setLocation] = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const lookup = searchParams.get("lookup") || "";
  const lookupValue = searchParams.get("lookupValue") || "";
  const initFromPathDone = useRef(false);

  const { data: settings } = trpc.setting.get.useQuery();
  const yearChoicesFromSettings = settings?.years
    ? Object.keys(settings.years)
        .map((year) => parseInt(year, 10))
        .filter((year) => !Number.isNaN(year))
    : [];
  const yearChoices =
    yearChoicesFromSettings.length > 0
      ? Array.from(new Set(yearChoicesFromSettings)).sort((a, b) => b - a)
      : [props.year];
  const selectedYear = yearChoices.includes(props.year)
    ? props.year
    : yearChoices[0];

  const pathYear = (() => {
    const yearSegment = location.split("/")[1] || "";
    const parsed = parseInt(yearSegment, 10);
    return Number.isNaN(parsed) ? null : parsed;
  })();
  const pathYearIsValid = pathYear !== null && yearChoices.includes(pathYear);

  const buildExaminerPath = (
    year: number,
    currentLookup: string,
    currentLookupValue: string,
  ) => {
    const params = new URLSearchParams();
    if (currentLookup.trim()) {
      params.set("lookup", currentLookup);
    }
    if (currentLookupValue.trim()) {
      params.set("lookupValue", currentLookupValue);
    }
    const query = params.toString();
    return `/${year}${query ? `?${query}` : ""}`;
  };

  const setLookups = ({
    nextLookup,
    nextLookupValue,
  }: {
    nextLookup?: string;
    nextLookupValue?: string;
  }) => {
    const params = new URLSearchParams(searchParams);
    const finalLookup = nextLookup ?? lookup;
    const finalLookupValue = nextLookupValue ?? lookupValue;

    if (finalLookup.trim()) {
      params.set("lookup", finalLookup);
    } else {
      params.delete("lookup");
    }

    if (finalLookupValue.trim()) {
      params.set("lookupValue", finalLookupValue);
    } else {
      params.delete("lookupValue");
    }

    setSearchParams(Object.fromEntries(params.entries()));
  };

  useEffect(() => {
    if (!initFromPathDone.current) {
      initFromPathDone.current = true;
      if (pathYear !== null && pathYearIsValid && pathYear !== selectedYear) {
        props.onSetYear(pathYear);
        return;
      }
    }

    if (selectedYear !== props.year) {
      props.onSetYear(selectedYear);
      return;
    }

    if (pathYear !== selectedYear) {
      setLocation(buildExaminerPath(selectedYear, lookup, lookupValue), {
        replace: true,
      });
    }
  }, [
    lookup,
    lookupValue,
    pathYear,
    pathYearIsValid,
    props.year,
    props.onSetYear,
    selectedYear,
    setLocation,
  ]);

  const factures = trpc.facture.getByYear.useQuery(selectedYear).data || [];
  const unjustifiedDepenses =
    trpc.banque.getUnjustifiedDepenses.useQuery().data || [];

  const normalizedLookup = lookup.trim().toLowerCase();
  const normalizedLookupValue = lookupValue.trim();
  const parsedLookupValueCent = floatStringToIntegerCent(normalizedLookupValue);
  const normalizedLookupValueCent =
    typeof parsedLookupValueCent === "number"
      ? Math.abs(parsedLookupValueCent)
      : null;
  const hasTextLookup = normalizedLookup.length > 1;
  const hasValueLookup =
    normalizedLookupValue.length > 0 && normalizedLookupValueCent !== null;
  const hasLookup = hasTextLookup || hasValueLookup;

  const filteredFactures = hasLookup
    ? factures.filter(
        (facture) =>
          (hasTextLookup &&
            facture.libellé.toLowerCase().includes(normalizedLookup)) ||
          (hasValueLookup &&
            (Math.abs(facture.montantCent) === normalizedLookupValueCent ||
              facture.règlements.some(
                (règlement) =>
                  Math.abs(règlement.montantCent) === normalizedLookupValueCent,
              ))),
      )
    : [];

  const sortedFactures = _.sortBy(
    filteredFactures,
    (facture) =>
      facture.date?.getTime() || facture.règlements[0]?.date?.getTime() || 0,
  );

  const matchedBanqueLineIds = hasLookup
    ? Array.from(
        new Set(
          filteredFactures.flatMap((facture) => {
            const ids = [facture.banqueId || ""];
            facture.règlements.forEach((règlement) => {
              ids.push(règlement.banqueId || "");
            });
            return ids.filter((id) => !!id);
          }),
        ),
      )
    : [];

  const { data: matchedBanqueLines } = trpc.banque.getBanqueLines.useQuery(
    matchedBanqueLineIds,
    {
      enabled: matchedBanqueLineIds.length > 0,
    },
  );

  const matchedBanqueLinesById = new Map(
    (matchedBanqueLines || []).map((depense) => [depense._id, depense]),
  );

  const facturesWithMatchedDepenses = sortedFactures.map((facture) => {
    const relatedBanqueLineIds = Array.from(
      new Set(
        [
          facture.banqueId || "",
          ...facture.règlements.map((règlement) => règlement.banqueId || ""),
        ].filter((id) => !!id),
      ),
    );

    const matchedDepensesForFacture = _.sortBy(
      relatedBanqueLineIds.flatMap((banqueLineId) => {
        const depense = matchedBanqueLinesById.get(banqueLineId);
        return depense ? [depense] : [];
      }),
      (depense) => depense.date.getTime(),
    );

    return {
      facture,
      matchedDepensesForFacture,
    };
  });

  const filteredUnmatchedDepenses = hasLookup
    ? unjustifiedDepenses.filter(
        (depense) =>
          ((hasTextLookup &&
            depense.libelle.toLowerCase().includes(normalizedLookup)) ||
            (hasValueLookup &&
              Math.abs(depense.montantCent) === normalizedLookupValueCent)) &&
          depense.date.getFullYear() === selectedYear,
      )
    : [];

  const sortedUnmatchedDepenses = _.sortBy(
    filteredUnmatchedDepenses,
    (depense) => depense.date.getTime(),
  );

  const onFactureClicked = (factureId: string) => {
    setLocation("~/factures/factureInput?factureId=" + factureId, {
      state: "back",
    });
  };

  return (
    <div>
      <div className="mb-2 d-flex align-items-center gap-2">
        <span>Année examinée :</span>
        <select
          className="form-select form-select-sm w-auto"
          value={selectedYear}
          onChange={(event) => {
            const nextYear = parseInt(event.target.value, 10);
            props.onSetYear(nextYear);
            setLocation(buildExaminerPath(nextYear, lookup, lookupValue));
          }}
        >
          {yearChoices.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
      <div className="row g-2 mb-3">
        <div className="col-12 col-lg-8">
          <input
            type="text"
            className="form-control"
            placeholder="Rechercher un libellé de facture"
            value={lookup}
            onChange={(e) => setLookups({ nextLookup: e.target.value })}
          />
        </div>
        <div className="col-12 col-lg-4">
          <input
            type="text"
            className="form-control"
            placeholder="Rechercher une valeur (ex: 120,50)"
            value={lookupValue}
            onChange={(e) => setLookups({ nextLookupValue: e.target.value })}
          />
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <div className="row g-2">
                <div className="col-12 col-lg-6 d-flex justify-content-between align-items-center">
                  <span>Factures rentrées</span>
                  <span className="badge text-bg-secondary">
                    {sortedFactures.length}
                  </span>
                </div>
                <div className="col-12 col-lg-6 d-flex justify-content-between align-items-center">
                  <span>Lignes bancaires rapprochées</span>
                  <span className="badge text-bg-secondary">
                    {matchedBanqueLinesById.size}
                  </span>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              {sortedFactures.length > 0 ? (
                facturesWithMatchedDepenses.map(
                  ({ facture, matchedDepensesForFacture }) => (
                    <div key={facture._id} className="row g-0 border-top">
                      <div className="col-12 col-lg-6 p-3 border-end">
                        <FactureComp
                          facture={facture}
                          showModeBadge
                          onFactureClicked={onFactureClicked}
                        />
                      </div>
                      <div className="col-12 col-lg-6 p-0">
                        {matchedDepensesForFacture.length > 0 ? (
                          <ul className="list-group list-group-flush">
                            {matchedDepensesForFacture.map((banqueLine) => (
                              <DepenseLine
                                key={banqueLine._id}
                                {...banqueLine}
                                showTypeBadge
                                selected={false}
                                onVentiler={() => {}}
                              />
                            ))}
                          </ul>
                        ) : (
                          <div className="p-3 text-muted small">
                            Aucune ligne rapprochée pour cette facture.
                          </div>
                        )}
                      </div>
                    </div>
                  ),
                )
              ) : (
                <div className="p-3 text-muted small">
                  Aucune facture à afficher (entrez au moins 2 caractères dans
                  la recherche texte ou une valeur).
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6 offset-lg-6">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span>Lignes bancaires non rapprochées</span>
              <span className="badge text-bg-secondary">
                {sortedUnmatchedDepenses.length}
              </span>
            </div>
            <div className="card-body p-0">
              {sortedUnmatchedDepenses.length > 0 ? (
                <ul className="list-group list-group-flush">
                  {sortedUnmatchedDepenses.map((banqueLine) => (
                    <DepenseLine
                      key={banqueLine._id}
                      {...banqueLine}
                      showTypeBadge
                      selected={false}
                      onVentiler={() => {}}
                    />
                  ))}
                </ul>
              ) : (
                <div className="p-3 text-muted small">
                  Aucune ligne non rapprochée pour la sélection.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
